/**
 * GridInstance
 * Manages a single dynamic-product-grid instance with independent state and restoration
 * History-only storage: products stored directly in GridInstance, then in history (no cache duplication)
 */
class GridInstance {
  constructor(gridElement) {
    if (!gridElement) {
      throw new Error("[GridInstance] gridElement is required");
    }

    // Grid identification
    this.gridElement = gridElement;
    this.gridId = gridElement.id;
    if (!this.gridId) {
      throw new Error("[GridInstance] Grid element must have an ID");
    }

    // Manager instances
    this.urlStateManager = null;
    this.dataManager = null;
    this.scrollManager = null;
    this.containerPool = null;

    // Configuration (use centralized config with fallbacks)
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};
    this.config = {
      initialSkeletonCount: cfg.INITIAL_ANIMATION_COUNT || 8,
      scrollSettleDelay: 0, // ScrollManager already has scroll end delay
      retryAttempts: cfg.RETRY_ATTEMPTS || 3,
      retryDelay: cfg.RETRY_DELAY || 1000,
    };

    // State
    this.isInitialized = false;
    this.isRestoring = false;
    this.scrollSettleTimeout = null;
    this.currentRetryCount = 0;
    this.initialTotalProducts = 0; // From Liquid data attribute
    this.productSource = "collection_page"; // Product source mode

    // Per-grid state
    this.gridOffsetTop = 0; // Position from document start
    this.height = 0; // Current grid height in pixels
    this.carouselIndex = 0; // First visible product index
    this.activeFeed = "collection"; // Feed type
    this.loadedCount = 0; // Products loaded
    this.totalCount = 0; // Total products available
    this.cacheKey = null; // Feed-specific cache key (kept for compatibility, but not used for storage)
    this.productId = null; // Product ID for recommendations mode
    
    // Product storage (history-only, no cache duplication)
    this.loadedProducts = []; // All loaded products (single source of truth)
    this.lastCursor = null; // Last cursor for pagination
    this.hasMore = false; // Whether more products are available
    this.filters = []; // Current filter state

    // DOM references
    this.loaderElement = null;

    // Bind methods
    this.handleVisibleRangeChange = this.handleVisibleRangeChange.bind(this);
    this.handleBoundaryReached = this.handleBoundaryReached.bind(this);
    this.handleProductsLoaded = this.handleProductsLoaded.bind(this);
    this.handleScrollEnd = this.handleScrollEnd.bind(this);
    this.handleFacetsUpdate = this.handleFacetsUpdate.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handlePageScroll = this.handlePageScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  /**
   * Initialize the grid instance
   */
  async initialize() {
    // Get loader element (grid-specific)
    const sectionId = this.gridElement.dataset.sectionId || this.gridElement.dataset.id;
    this.loaderElement = document.getElementById(`infinite-scroll-loader-${sectionId}`);

    // Read configuration from data attributes
    const productSource = this.gridElement.dataset.productSource || "collection_page";
    const collectionHandle = this.gridElement.dataset.collectionHandle;
    const productId = this.gridElement.dataset.productId;
    const columnsDesktop = parseInt(this.gridElement.dataset.columnsDesktop) || 4;
    const columnsMobile = parseInt(this.gridElement.dataset.columnsMobile) || 2;

    // Store the product source mode
    this.productSource = productSource;

    // Get initial total from Liquid (for immediate UI, before GraphQL returns)
    this.initialTotalProducts = parseInt(this.gridElement.dataset.totalProducts) || 50;

    // Calculate grid offsetTop from document start
    this.updateGridOffsetTop();

    // Initialize based on product source
    if (productSource === "recommended_products") {
      if (!productId) {
        return false;
      }
      return this.initializeRecommendationsMode(productId, columnsDesktop, columnsMobile);
    } else if (productSource === "static_collection") {
      if (!collectionHandle) {
        return false;
      }
      return this.initializeStaticCollectionMode(collectionHandle, columnsDesktop, columnsMobile);
    } else {
      // Default: collection_page mode
      if (!collectionHandle) {
        return false;
      }
      return this.initializeCollectionMode(collectionHandle, columnsDesktop, columnsMobile);
    }
  }

  /**
   * Update grid offsetTop from document start
   */
  updateGridOffsetTop() {
    const rect = this.gridElement.getBoundingClientRect();
    this.gridOffsetTop = rect.top + window.scrollY;
  }

  /**
   * Initialize for collection page mode (default)
   */
  async initializeCollectionMode(collectionHandle, columnsDesktop, columnsMobile) {
    // Initialize URLStateManager
    this.urlStateManager = new URLStateManager();

    // Generate feed-specific cache key
    const urlParams = this.urlStateManager.generateCacheKey();
    this.cacheKey = this.urlStateManager.generateCacheKey(
      window.location.href,
      this.gridId,
      "collection",
      collectionHandle
    );
    this.activeFeed = "collection";

    // Initialize DataManager (use centralized config)
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};
    this.dataManager = new DataManager({
      batchSize: cfg.BATCH_SIZE || 15,
      prefetchThreshold: cfg.PREFETCH_THRESHOLD || 0.8,
      onProductsLoaded: this.handleProductsLoaded,
      onFetchStart: () => this.showLoader(),
      onFetchEnd: () => this.hideLoader(),
      onError: this.handleError,
    });

    if (!this.dataManager.initialize(collectionHandle, this.urlStateManager, this.gridId, "collection")) {
      return false;
    }

    // Initialize ContainerPool (use centralized config)
    this.containerPool = new ContainerPool({
      parentSelector: `#${this.gridId}`,
      maxContainers: cfg.MAX_CONTAINERS || 50,
      itemHeight: this.calculateItemHeight(),
      columnsDesktop,
      columnsMobile,
    });

    // Initialize ScrollManager
    this.scrollManager = new ScrollManager({
      containerSelector: `#${this.gridId}`,
      itemHeight: this.calculateItemHeight(),
      columnsDesktop,
      columnsMobile,
      gridOffsetTop: this.gridOffsetTop,
      onVisibleRangeChange: this.handleVisibleRangeChange,
      onBoundaryReached: this.handleBoundaryReached,
      onScrollEnd: this.handleScrollEnd,
    });

    // Set initialized flag before loading data so callbacks can execute
    this.isInitialized = true;

    // Check if this is a restoration (GridManager coordinates, but we check flag)
    this.isRestoring = HistoryManager.isRestoration();

    if (this.isRestoring && window.gridManager) {
      // GridManager will coordinate restoration
      // Just prepare for it
      await this.prepareForRestoration();
    } else {
      await this.performFreshLoad();
    }

    // Setup event listeners (only in collection mode for facets)
    this.setupEventListeners();

    return true;
  }

  /**
   * Initialize for recommendations mode
   */
  async initializeRecommendationsMode(productId, columnsDesktop, columnsMobile) {
    // Track product ID for recommendations restoration
    this.productId = productId;

    // Generate feed-specific cache key
    this.cacheKey = `cache:${this.gridId}:recommendation:${productId}`;
    this.activeFeed = "recommendations";

    // Initialize DataManager for recommendations
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};
    this.dataManager = new DataManager({
      batchSize: cfg.BATCH_SIZE || 15,
      prefetchThreshold: cfg.PREFETCH_THRESHOLD || 0.8,
      onProductsLoaded: this.handleProductsLoaded,
      onFetchStart: () => this.showLoader(),
      onFetchEnd: () => this.hideLoader(),
      onError: this.handleError,
    });

    if (!this.dataManager.initializeForRecommendations(productId, this.gridId)) {
      return false;
    }

    // Initialize ContainerPool
    this.containerPool = new ContainerPool({
      parentSelector: `#${this.gridId}`,
      maxContainers: cfg.MAX_CONTAINERS || 50,
      itemHeight: this.calculateItemHeight(),
      columnsDesktop,
      columnsMobile,
    });

    // Initialize ScrollManager (no boundary handling for recommendations)
    this.scrollManager = new ScrollManager({
      containerSelector: `#${this.gridId}`,
      itemHeight: this.calculateItemHeight(),
      columnsDesktop,
      columnsMobile,
      gridOffsetTop: this.gridOffsetTop,
      onVisibleRangeChange: this.handleVisibleRangeChange,
      onBoundaryReached: () => { }, // No infinite loading for recommendations
      onScrollEnd: this.handleScrollEnd,
    });

    this.isInitialized = true;

    // Initialize container pool with estimate
    this.containerPool.initialize(this.initialTotalProducts, []);
    this.scrollManager.initialize(this.initialTotalProducts);

    // Check if this is a restoration (GridManager coordinates, but we check flag)
    this.isRestoring = HistoryManager.isRestoration();

    if (this.isRestoring && window.gridManager) {
      // GridManager will coordinate restoration via performRestoration()
      // Products will come from history stack (sessionStorage), not GraphQL
      await this.prepareForRestoration();
    } else {
      // Fresh load - fetch expanded recommendations via GraphQL
      await this.performFreshRecommendationsLoad(productId);
    }

    return true;
  }

  /**
   * Perform fresh load for recommendations (not restoration)
   * Fetches recommendations via GraphQL
   */
  async performFreshRecommendationsLoad(productId) {
    try {
      this.showLoader();
      const products = await this.dataManager.fetchExpandedRecommendations(productId, false);
      this.hideLoader();

      if (products.length > 0) {
        // Store products (history-only, no cache)
        this.loadedProducts = products;
        this.hasMore = false; // Recommendations are fully loaded
        this.lastCursor = null;
        
        // Update container pool with all products
        this.containerPool.setProductData(products);
        this.containerPool.setTotalItems(products.length);
        this.containerPool.setLoadedCount(products.length);

        // Update scroll manager
        this.scrollManager.setTotalItems(products.length);
        this.scrollManager.setLoadedCount(products.length);

        // Update grid state
        this.loadedCount = products.length;
        this.totalCount = products.length;
        this.updateHeight();

        // Calculate and render initial visible range
        const relativeScrollTop = this.calculateRelativeScrollTop();
        const visibleRange = this.scrollManager.calculateVisibleRange(relativeScrollTop);
        this.handleVisibleRangeChange(visibleRange);

        // Update history immediately with products (history-only storage)
        if (window.gridManager) {
          window.gridManager.updateHistoryState();
        }
      } else {
        this.showErrorMessage("No recommended products found.");
      }
    } catch (error) {
      this.hideLoader();
      this.showErrorMessage("Failed to load recommended products.");
    }
  }

  /**
   * Initialize for static collection mode
   * Static collections use simple cache keys without URL dependencies
   */
  async initializeStaticCollectionMode(collectionHandle, columnsDesktop, columnsMobile) {
    this.activeFeed = "collection";

    // Initialize DataManager for static collection (creates simple cache key)
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};
    this.dataManager = new DataManager({
      batchSize: cfg.BATCH_SIZE || 15,
      prefetchThreshold: cfg.PREFETCH_THRESHOLD || 0.8,
      onProductsLoaded: this.handleProductsLoaded,
      onFetchStart: () => this.showLoader(),
      onFetchEnd: () => this.hideLoader(),
      onError: this.handleError,
    });

    if (!this.dataManager.initializeForStaticCollection(collectionHandle, this.gridId)) {
      return false;
    }

    // Use DataManager's cache key (simple format: cache:gridId:collection:handle)
    // Static collections should NEVER use URL-based keys
    this.cacheKey = this.dataManager.cacheKey;
    
    // Validate cache key format for static collections
    const expectedPattern = new RegExp(`^cache:${this.gridId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:collection:[^:]+$`);
    if (!expectedPattern.test(this.cacheKey)) {
      // Cache key format validation failed
    }

    // Initialize ContainerPool
    this.containerPool = new ContainerPool({
      parentSelector: `#${this.gridId}`,
      maxContainers: cfg.MAX_CONTAINERS || 50,
      itemHeight: this.calculateItemHeight(),
      columnsDesktop,
      columnsMobile,
    });

    // Initialize ScrollManager
    this.scrollManager = new ScrollManager({
      containerSelector: `#${this.gridId}`,
      itemHeight: this.calculateItemHeight(),
      columnsDesktop,
      columnsMobile,
      gridOffsetTop: this.gridOffsetTop,
      onVisibleRangeChange: this.handleVisibleRangeChange,
      onBoundaryReached: this.handleBoundaryReached,
      onScrollEnd: this.handleScrollEnd,
    });

    this.isInitialized = true;

    // Initialize container pool with Liquid's total as initial estimate
    this.containerPool.initialize(this.initialTotalProducts, []);
    this.scrollManager.initialize(this.initialTotalProducts);

    // Check if restoration (GridManager coordinates)
    this.isRestoring = HistoryManager.isRestoration();

    if (this.isRestoring && window.gridManager) {
      // GridManager coordinates restoration
      await this.prepareForRestoration();
    } else {
      // Fetch initial batch
      try {
        await this.fetchWithRetry(() => this.dataManager.fetchInitialBatch());
      } catch (error) {
        this.showErrorMessage("Failed to load products. Please refresh the page.");
      }
    }

    return true;
  }

  /**
   * Perform fresh load (not restoration)
   */
  async performFreshLoad() {
    // Initialize container pool with Liquid's total as initial estimate
    this.containerPool.initialize(this.initialTotalProducts, []);

    // Initialize scroll manager with estimate
    this.scrollManager.initialize(this.initialTotalProducts);

    // Fetch initial data - this will update totalCount from GraphQL
    try {
      await this.fetchWithRetry(() => this.dataManager.fetchInitialBatch());
    } catch (error) {
      this.showErrorMessage(
        "Failed to load products. Please refresh the page."
      );
    }
  }

  /**
   * Prepare for restoration (called by GridManager)
   */
  async prepareForRestoration() {
    // This will be called by GridManager.performRestoration()
    // Just mark as restoring
    this.isRestoring = true;
  }

  /**
   * Fast layout restoration - sets height immediately before data processing
   * Called first to make page appear in correct position quickly
   * @param {Object} gridState - Grid state with height
   */
  prepareRestorationLayout(gridState) {
    if (!gridState || !gridState.height || gridState.height <= 0) {
      return false;
    }

    // Get or create container element
    const container = document.querySelector(`#${this.gridId}`);
    if (!container) {
      return false;
    }

    // Set height immediately (synchronous, fast)
    this.height = gridState.height;
    container.style.height = `${gridState.height}px`;
    
    return true;
  }

  /**
   * Perform restoration from back navigation (called by GridManager)
   * Uses products from history snapshot for self-contained restoration.
   * 
   * @param {Object} gridState - Grid state from history: { height, carouselIndex, activeFeed, loadedCount, totalCount, products }
   *   - products: Array of slimmed products for self-contained restoration (required)
   */
  async performRestoration(gridState) {
    if (!gridState) {
      this.isRestoring = false;
      return this.performFreshLoad();
    }

    const isRecommendations = this.productSource === "recommended_products";

    // ─────────────────────────────────────────────────────────────
    // HISTORY PRODUCTS RESTORATION (self-contained from snapshot)
    // ─────────────────────────────────────────────────────────────
    const historyProducts = gridState.products;
    
    if (!historyProducts || historyProducts.length === 0) {
      this.isRestoring = false;
      return this.performFreshLoad();
    }

    // Use products from history snapshot - this is the source of truth
    const effectiveTotal =
      gridState.totalCount || historyProducts.length || this.initialTotalProducts;
    const loadedCount = gridState.loadedCount || historyProducts.length;

    // Initialize with history products
    this.containerPool.initialize(effectiveTotal, historyProducts);
    this.containerPool.setProductData(historyProducts);
    this.containerPool.setLoadedCount(loadedCount);

    this.scrollManager.initialize(effectiveTotal);
    this.scrollManager.setLoadedCount(loadedCount);

    // Set grid height from history if available (only if not already set by prepareRestorationLayout)
    if (gridState.height && gridState.height > 0 && !this.height) {
      this.height = gridState.height;
      this.scrollManager.scrollContainer.style.height = `${gridState.height}px`;
    } else if (this.height && this.scrollManager.scrollContainer) {
      // Ensure ScrollManager container has the height that was set earlier
      this.scrollManager.scrollContainer.style.height = `${this.height}px`;
    }

    // Restore per-grid state
    this.loadedCount = loadedCount;
    this.totalCount = effectiveTotal;
    this.carouselIndex = gridState.carouselIndex || 0;
    this.activeFeed = gridState.activeFeed || this.activeFeed;
    
    // Restore products and pagination state from history
    this.loadedProducts = historyProducts;
    this.lastCursor = gridState.lastCursor || null;
    this.hasMore = gridState.hasMore || false;
    this.filters = gridState.filters || [];
    
    // Also restore state in DataManager for consistency
    if (this.dataManager) {
      this.dataManager.loadedProducts = historyProducts;
      this.dataManager.lastCursor = gridState.lastCursor || null;
      this.dataManager.hasMore = gridState.hasMore || false;
      this.dataManager.filters = gridState.filters || [];
      this.dataManager.totalCount = effectiveTotal;
    }

    // Calculate visible range based on carouselIndex
    const columns = this.scrollManager.getColumns();
    const row = Math.floor(this.carouselIndex / columns);
    const targetScrollTop = row * this.scrollManager.rowHeight;
    const visibleRange =
      this.scrollManager.calculateVisibleRange(targetScrollTop);
    this.handleVisibleRangeChange(visibleRange);

    // Wait for layout to stabilize before GM restores page scroll
    await this.waitForLayout();

    // Note: Grid scroll position will be verified after page scroll is restored
    // by GridManager using restoreScrollWithRetry for verification
    this.isRestoring = false;
    return true;
  }

  /**
   * Calculate relative scroll top for this grid using offsetTop
   * @returns {number} Relative scroll position
   */
  calculateRelativeScrollTop() {
    const containerRect = this.scrollManager.scrollContainer.getBoundingClientRect();

    // If grid is above viewport, calculate from offsetTop
    if (containerRect.top < 0) {
      return Math.max(0, window.scrollY - this.gridOffsetTop);
    }

    // If grid is in or below viewport, use standard calculation
    return Math.max(0, -containerRect.top);
  }

  /**
   * Handle page scroll (called by GridManager)
   */
  handlePageScroll() {
    if (!this.isInitialized || this.isRestoring) return;

    // Avoid updating virtual grid before any products are loaded.
    // Early user scrolls during initial fetch should not move the grid.
    if (!this.loadedCount) return;

    // Update grid offsetTop (may change on resize)
    this.updateGridOffsetTop();

    // Calculate relative scroll position
    const relativeScrollTop = this.calculateRelativeScrollTop();
    this.scrollManager.scrollTop = relativeScrollTop;

    // Calculate visible range
    const visibleRange = this.scrollManager.calculateVisibleRange(relativeScrollTop);

    // Notify listeners
    if (this.scrollManager.onVisibleRangeChange) {
      this.scrollManager.onVisibleRangeChange(visibleRange);
    }

    // Check boundary
    this.scrollManager.checkBoundary(visibleRange);

    // Handle scroll end detection
    this.scrollManager.detectScrollEnd();
  }

  /**
   * Handle window resize
   */
  handleResize() {
    if (!this.isInitialized) return;

    // Update grid offsetTop
    this.updateGridOffsetTop();
    this.scrollManager.gridOffsetTop = this.gridOffsetTop;

    // Update dimensions
    this.scrollManager.updateDimensions();
    this.containerPool.updateDimensions();

    // Recalculate height
    this.updateHeight();

    // Recalculate visible range
    this.handlePageScroll();
  }

  /**
   * Calculate and update grid height
   */
  updateHeight() {
    if (!this.scrollManager || !this.scrollManager.isInitialized) return;

    const columns = this.scrollManager.getColumns();
    const totalRows = Math.ceil(this.loadedCount / columns);
    const calculatedHeight = totalRows * this.scrollManager.rowHeight;

    this.height = calculatedHeight;
    this.scrollManager.scrollContainer.style.height = `${calculatedHeight}px`;
  }

  /**
   * Get current grid state
   * @returns {Object} Grid state
   */
  getState() {
    return {
      gridId: this.gridId,
      carouselIndex: this.carouselIndex,
      activeFeed: this.activeFeed,
      height: this.height,
      loadedCount: this.loadedCount,
      totalCount: this.totalCount,
      gridOffsetTop: this.gridOffsetTop,
    };
  }

  /**
   * Get history state snapshot including products for self-contained restoration
   * Reads from GridInstance state (history-only, no cache)
   * @returns {Object} History state with products array and pagination state
   */
  getHistoryState() {
    const baseState = {
      carouselIndex: this.carouselIndex,
      activeFeed: this.activeFeed,
      height: this.height,
      loadedCount: this.loadedCount,
      totalCount: this.totalCount,
      products: this.loadedProducts || [], // From GridInstance state
      lastCursor: this.lastCursor, // For pagination
      hasMore: this.hasMore, // For prefetch logic
      filters: this.filters || [], // Filter state
    };

    return baseState;
  }


  /**
   * Fetch with retry logic
   */
  async fetchWithRetry(fetchFn) {
    this.currentRetryCount = 0;

    while (this.currentRetryCount < this.config.retryAttempts) {
      try {
        return await fetchFn();
      } catch (error) {
        this.currentRetryCount++;

        if (this.currentRetryCount < this.config.retryAttempts) {
          await this.delay(this.config.retryDelay * this.currentRetryCount);
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    document.addEventListener("facets:updated", this.handleFacetsUpdate);
    window.addEventListener("facets:updated", this.handleFacetsUpdate);
  }

  /**
   * Handle visible range change from ScrollManager
   */
  handleVisibleRangeChange(range) {
    if (!this.isInitialized) return;

    // Update container pool with new visible range
    const bufferRange = this.scrollManager.calculateBufferRange(
      range,
      this.containerPool.containerCount
    );

    this.containerPool.updateVisibleRange(bufferRange);

    // Check if we need to prefetch
    const currentIndex = range.end;
    // IMPORTANT: no prefetch for recommendations grid
    // Recommendations are fully loaded up front by fetchExpandedRecommendations().
    if (this.productSource !== "recommended_products" && this.dataManager) {
      this.dataManager.prefetchIfNeeded(currentIndex);
    }
  }

  /**
   * Handle boundary reached from ScrollManager
   */
  handleBoundaryReached(boundary, info) {
    if (boundary === "bottom" &&
      !this.dataManager.isComplete() &&
      !this.dataManager.isFetching) {
      this.fetchWithRetry(() => this.dataManager.fetchNextBatch()).catch(
        this.handleError
      );
    }
  }

  /**
   * Handle products loaded from DataManager
   * Stores products in GridInstance (history-only, no cache duplication)
   * Immediately updates history with new products
   */
  handleProductsLoaded(data) {
    const { allProducts, loadedCount, totalCount, hasMore, cursor, filters } = data;

    // Store products and pagination state (history-only storage)
    this.loadedProducts = allProducts;
    this.loadedCount = loadedCount;
    this.totalCount = totalCount;
    this.hasMore = hasMore || false;
    if (cursor !== undefined) {
      this.lastCursor = cursor;
    }
    if (filters) {
      this.filters = filters;
    }

    // Update container pool with new data
    this.containerPool.setProductData(allProducts);
    this.containerPool.setTotalItems(totalCount);
    this.containerPool.setLoadedCount(loadedCount);

    // Update scroll manager - totalCount for height (fixed), loadedCount for bounds
    this.scrollManager.setTotalItems(totalCount);
    this.scrollManager.setLoadedCount(loadedCount);

    // Update height (tracked on data load)
    this.updateHeight();

    // Get scroll position relative to container (window scroll mode)
    const relativeScrollTop = this.calculateRelativeScrollTop();

    // Calculate and render initial visible range
    const visibleRange =
      this.scrollManager.calculateVisibleRange(relativeScrollTop);

    this.handleVisibleRangeChange(visibleRange);

    // Update history immediately with products (history-only storage)
    // This ensures history always has current products, even before scroll
    if (window.gridManager && !this.isRestoring) {
      window.gridManager.updateHistoryState();
    }
  }

  /**
   * Handle scroll end - update history state
   */
  handleScrollEnd(scrollTop) {
    if (this.scrollSettleTimeout) {
      clearTimeout(this.scrollSettleTimeout);
    }

    this.scrollSettleTimeout = setTimeout(() => {
      this.updateHistoryState();
    }, this.config.scrollSettleDelay);
  }

  /**
   * Update history state with current scroll position and height
   */
  updateHistoryState() {
    if (!this.isInitialized || this.isRestoring) return;

    // Update carouselIndex
    this.carouselIndex = this.scrollManager.getCurrentIndex();

    // Update height (tracked on scroll end)
    this.updateHeight();

    // Notify GridManager to update history (GridManager collects all grid states)
    if (window.gridManager) {
      window.gridManager.updateHistoryState();
    }
  }

  /**
   * Handle facets update (filter/sort change)
   */
  async handleFacetsUpdate(event) {
    // Only handle facets in collection_page mode
    if (this.productSource !== "collection_page") {
      return;
    }

    // Update URL state manager
    this.urlStateManager.update();

    // Generate new feed-specific cache key
    const collectionHandle = this.gridElement.dataset.collectionHandle;
    const newCacheKey = this.urlStateManager.generateCacheKey(
      window.location.href,
      this.gridId,
      "collection",
      collectionHandle
    );
    this.cacheKey = newCacheKey;

    // Reset container pool (show skeletons)
    this.containerPool.reset();

    // Reset data manager with new cache key
    this.dataManager.reset(newCacheKey);

    // Reset scroll to top (window scroll mode)
    window.scrollTo(0, 0);
    this.scrollManager.scrollToIndex(0, "instant");

    // Reset grid state
    this.carouselIndex = 0;
    this.height = 0;
    this.loadedCount = 0;
    this.totalCount = 0;
    this.loadedProducts = [];
    this.lastCursor = null;
    this.hasMore = false;
    this.filters = [];

    // Fetch new data - this will get new totalCount from filters
    // handleProductsLoaded will update history immediately with new products
    // updateCurrentState() will replace the current history entry (same URL, new filters)
    try {
      await this.fetchWithRetry(() => this.dataManager.fetchInitialBatch());
    } catch (error) {
      this.showErrorMessage("Failed to load products. Please try again.");
    }
  }

  /**
   * Handle errors
   */
  handleError(error) {
    // Error handled silently
  }

  /**
   * Show loading indicator
   */
  showLoader() {
    if (this.loaderElement) {
      this.loaderElement.classList.add("visible");
    }
  }

  /**
   * Hide loading indicator
   */
  hideLoader() {
    if (this.loaderElement) {
      this.loaderElement.classList.remove("visible");
    }
  }

  /**
   * Show error message to user (per-grid)
   */
  showErrorMessage(message) {
    // Use grid-specific error element
    const errorId = `infinite-scroll-error-${this.gridId}`;
    let errorEl = document.getElementById(errorId);
    if (!errorEl) {
      errorEl = document.createElement("div");
      errorEl.id = errorId;
      errorEl.className = "infinite-scroll-error";
      this.gridElement.parentNode.insertBefore(errorEl, this.gridElement);
    }

    errorEl.innerHTML = `
      <div class="error-content">
        <p>${message}</p>
        <button onclick="window.location.reload()" class="button">Reload Page</button>
      </div>
    `;
    errorEl.style.display = "block";
  }

  /**
   * Calculate item height based on viewport (use centralized config)
   */
  calculateItemHeight() {
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};
    const breakpoint = cfg.MOBILE_BREAKPOINT || 750;
    const isMobile = window.innerWidth < breakpoint;
    return isMobile
      ? cfg.ITEM_HEIGHT_MOBILE || 280
      : cfg.ITEM_HEIGHT_DESKTOP || 320;
  }

  /**
   * Utility: delay promise
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Wait for browser layout to stabilize (double requestAnimationFrame)
   * @returns {Promise<void>}
   */
  waitForLayout() {
    return new Promise((resolve) => {
        requestAnimationFrame(resolve);
    });
  }

  /**
   * Verify grid scroll position matches target index (without scrolling)
   * @param {number} targetIndex - Target carousel index
   * @returns {boolean} - Whether position matches
   */
  verifyScrollPosition(targetIndex) {
    if (!this.scrollManager || !this.scrollManager.isInitialized) {
      return false;
    }

    const currentIndex = this.scrollManager.getCurrentIndex();
    const columns = this.scrollManager.getColumns();

    // Allow tolerance of one row
    const matches = Math.abs(currentIndex - (targetIndex || 0)) <= columns;
    
    return matches;
  }

  /**
   * Restore scroll position with retry and verification
   * @param {number} targetIndex - Target carousel index
   * @param {number|null} targetScrollY - Target window scroll Y position
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {Promise<boolean>} - Whether restoration succeeded
   */
  async restoreScrollWithRetry(targetIndex, targetScrollY, maxRetries = 3) {
    for (let i = 0; i < maxRetries; i++) {
      // Wait for layout to stabilize
      await this.waitForLayout();

      // Attempt scroll
      if (targetIndex !== null && targetIndex !== undefined) {
        this.scrollManager.scrollToIndex(targetIndex, "instant");
      }
      if (targetScrollY) {
        window.scrollTo(0, targetScrollY);
      }

      // Wait for scroll to complete
      await this.waitForLayout();

      // Verify scroll position
      const currentIndex = this.scrollManager.getCurrentIndex();
      const columns = this.scrollManager.getColumns();

      // Allow tolerance of one row
      if (Math.abs(currentIndex - (targetIndex || 0)) <= columns) {
        return true;
      }
    }

    return false;
  }

  /**
   * Destroy the controller and cleanup
   */
  destroy() {
    if (this.scrollSettleTimeout) {
      clearTimeout(this.scrollSettleTimeout);
    }

    document.removeEventListener("facets:updated", this.handleFacetsUpdate);
    window.removeEventListener("facets:updated", this.handleFacetsUpdate);

    if (this.scrollManager) this.scrollManager.destroy();
    if (this.containerPool) this.containerPool.destroy();

    this.isInitialized = false;
  }
}

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = GridInstance;
}

// Keep InfiniteScrollController as alias for backward compatibility
if (typeof InfiniteScrollController === "undefined") {
  window.InfiniteScrollController = GridInstance;
}
// Expose GridInstance globally for GridManager
window.GridInstance = GridInstance;

